import { Label } from '@kmgestao/ui';

import type { FormField } from '../../schemas.js';

export interface MultiselectFieldProps {
  field: FormField;
  /** Array dos `option.value` selecionados. Aceita `string` por
   * compat com backends que armazenam array como jsonb — o
   * renderer converte para array no onChange. */
  value: string[] | string;
  onChange: (next: string[]) => void;
  onBlur?: (() => void) | undefined;
  error?: string | undefined;
  disabled?: boolean | undefined;
  idPrefix?: string | undefined;
}

/**
 * Grupo de checkboxes — múltipla escolha. Diferente de `select` que
 * aceita 1 valor, aqui o user pode marcar várias opções.
 *
 * **Mobile-first**: cada opção é um label inteiro clicável com
 * `min-h-11` (≥ 44px), não só o checkbox pequeno. Borda destaca a
 * seleção ativa. Consistente visualmente com `RadioField` — só muda
 * a semântica (múltipla vs única).
 *
 * **Visibilidade condicional**: o `field.other_field` (campo auxiliar
 * quando "outro" é selecionado) é renderizado pelo pai `FormFieldRenderer`
 * via recursão — este componente só se preocupa com a lista principal.
 */
export function MultiselectField({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  idPrefix = 'form',
}: MultiselectFieldProps) {
  const groupId = `${idPrefix}-${field.id}`;
  const labelId = `${groupId}-lbl`;
  const descId = field.description ? `${groupId}-desc` : undefined;
  const errorId = error ? `${groupId}-err` : undefined;
  const options = field.options ?? [];

  // Normaliza `value` para array — prefill pode vir como string vazia
  // se o banco tiver default `''::jsonb` em vez de `[]`.
  const selected: string[] = Array.isArray(value) ? value : [];

  const toggle = (optValue: string) => {
    if (selected.includes(optValue)) {
      onChange(selected.filter((v) => v !== optValue));
    } else {
      onChange([...selected, optValue]);
    }
  };

  return (
    <div className="space-y-2">
      <Label id={labelId}>
        {field.label}
        {field.required ? <span className="ml-0.5 text-destructive"> *</span> : null}
      </Label>
      {field.description ? (
        <p id={descId} className="text-xs text-muted-foreground">
          {field.description}
        </p>
      ) : null}
      <div
        role="group"
        aria-labelledby={labelId}
        aria-describedby={[descId, errorId].filter(Boolean).join(' ') || undefined}
        className="space-y-2"
      >
        {options.map((opt, idx) => {
          const optId = `${groupId}-opt-${idx}`;
          const checked = selected.includes(opt.value);
          return (
            <label
              key={opt.value}
              htmlFor={optId}
              className={[
                'flex min-h-11 cursor-pointer items-center gap-3 rounded-md border bg-background px-3 py-2',
                'text-base transition-colors hover:bg-muted/50',
                'disabled:cursor-not-allowed disabled:opacity-50',
                checked ? 'border-primary ring-1 ring-primary/20' : 'border-input',
              ].join(' ')}
            >
              <input
                id={optId}
                type="checkbox"
                value={opt.value}
                checked={checked}
                onChange={() => toggle(opt.value)}
                onBlur={onBlur}
                disabled={disabled}
                className="size-4 accent-primary"
              />
              <span>{opt.label}</span>
            </label>
          );
        })}
      </div>
      {error ? (
        <p id={errorId} className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
